import { supertypeClass, property, amorphicStatic } from '@haventech/amorphic';
import { IsomorphicPersistent } from '@hit/amorphic-types';
import { Party } from '../customer/Party';
import { Person } from '../customer/Person';
import { AgentProfile, PrincipalType } from '../customer/AgentProfile';
import { Phone } from '../customer/Phone';
import { serverOnly } from '../helpers';
import { CdmService } from '../../../common/js/services/CdmService';
import { LoggerSingleton, Category } from '../logger/LoggerSingleton';

@supertypeClass({ toClient: true, toServer: true })
export class AgentProxy extends IsomorphicPersistent {
  @property({ rule: ['text', 'required'], comment: 'Serving proxy Id number' })
  proxyId: string;

  @property({ comment: 'true if the proxy logged in at least once' })
  hasEverLoggedIn: boolean;

  @property({ comment: 'true if the proxy attested that they will not misconduct' })
  hasAttested: boolean;

  /* Personal info for the agent.*/
  @property({ getType: () => Party, comment: 'Personal info for the proxy' })
  party: Party;

  /**
   * @deprecated - Use groups instead
   */
  @property({ toServer: false, comment: 'The data.group from SAML this Proxy is part of' })
  group: string;

  // This will capture all groups the logged in user belongs to. The above group column is deprecated and
  // will be removed next sprint
  @property({
    toServer: false,
    type: Array,
    getType: () => String,
    comment:
      'The data.groups from SAML this Agent is part of. This is an array of strings like fim-coverpath-agent, fim-coverpath-help etc',
  })
  groups: string[];

  @property({ getType: () => AgentProfile })
  profile: AgentProfile;

  @property({ toServer: false, comment: 'Indicates the producerId mocked for local use' })
  mockedProducerId?: string = '';

  /* The agency id from CDM. */
  @property({ toServer: false, rule: ['text', 'required'], comment: 'Serving agency Id number' })
  agencyId: string;

  /* The agency name from CDM. */
  @property({ toServer: false, rule: ['text', 'required'], comment: 'Serving agency Name' })
  agencyName: string;

  /* The merged agency ids from CDM. */
  @property({ toServer: false, type: Array, persist: false, comment: 'Merged Agency Ids', getType: () => String })
  mergedAgencyIds: Array<string> = [];

  constructor() {
    super();
  }

  static new(proxyId: string) {
    return new AgentProxy().init(proxyId);
  }

  init(proxyId: string) {
    this.proxyId = proxyId;
    this.party = Person.new();
    this.phone = Phone.new();
    this.profile = AgentProfile.new(this, PrincipalType.Proxy);
    return this;
  }

  get producerId(): string {
    return this.proxyId;
  }

  get person(): Person {
    return this.party as Person;
  }

  get phone(): Phone {
    return this.party.phone;
  }

  set phone(phone: Phone) {
    this.party.phone = phone;
  }

  get displayNameWithLastName(): string {
    return `${this.displayName} ${this.person.lastName}`.trim();
  }

  get displayName(): string {
    return (this.profile && this.profile.displayName) || this.person.firstName;
  }

  // Agency ids are 6 digits but they are padded to the left with 4 leading 0.
  // This function trims the agency id and only returns the last 6 'digits' of it.
  get agencyIdTrimmed() {
    if (this.agencyId) {
      return this.agencyId.slice(-6);
    }
    return null;
  }

  @serverOnly()
  async updateContactInfoFromCDM(): Promise<void> {
    try {
      const cdmUserResponse = await new CdmService().getUserInfoFromCDM(this.proxyId);
      const agency = cdmUserResponse.agency;
      if (agency) {
        this.agencyId = agency.businessPartnerId;
        this.agencyName = agency.name;
        try {
          const agencyMergers = await new CdmService().getAgencyMergersFromCdm([this.agencyId]);

          if (agencyMergers.length === 1) {
            this.mergedAgencyIds = agencyMergers[0];
          }
        } catch (err) {
          LoggerSingleton.getInstance().warn(
            {
              module: 'AgentProxy',
              function: 'updateContactInfoFromCDM',

              category: Category.request,
              error: {
                name: err?.name,
                message: err?.message,
                stack: err?.stack,
                isHumanRelated: false,
                data: {
                  producerId: this.producerId,
                },
              },
            },
            'Failed to get agency mergers from CDM'
          );
          return;
        }
      }
      return;
    } catch (err) {
      // Eat up the error because not being able to get contact info is
      // non fatal and the agent-proxy can continue using the app to some extent
      LoggerSingleton.getInstance().warn(
        {
          module: 'AgentProxy',
          function: 'updateContactInfoFromCDM',

          category: Category.request,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
            data: {
              producerId: this.producerId,
            },
          },
        },
        'Failed to update agent proxy info with latest data from CDM.'
      );

      return;
    }
  }
}
